import type {
  AdminAuditEntry,
  AdminAuditFilters,
  AdminCentralSubscription,
  AdminCentralSubscriptionRenewPayload,
  AdminDashboardAnalytics,
  AdminModerator,
  AdminModeratorPayload,
  AdminNotification,
  AdminNotificationSendPayload,
  AdminNotificationSendResult,
  AdminPackage,
  AdminPackagePayload,
  AdminPendingFieldChange,
  AdminPromoCode,
  AdminPromoCodePayload,
  AdminRole,
  AdminSession,
  AdminSessionDetail,
  AdminSessionsAnalytics,
  AdminSettingEntry,
  AdminSettingsGroups,
  AdminSubscription,
  AdminSubscriptionPayload,
  AdminDoctorSubscription,
  AdminSubscriptionRequest,
  AdminZonePrice,
  AdminZonePricePayload,
  TopCentral,
} from "@/types/admin";
import { isAxiosError } from "axios";
import type { ListParams, Paginated } from "../../api";
import { normalizePaginated, unwrapItem } from "../../apiNormalize";
import { buildFilterQuery, buildQuery, del, get, post, put } from "../helpers";

import type { AdminDashboardSummary } from "@/types/domain";

// ——— Dashboard ———

export async function fetchAdminDashboard(): Promise<AdminDashboardSummary> {
  const body = await get<unknown>("/admin/dashboard");
  return unwrapItem<AdminDashboardSummary>(body);
}

export async function fetchAdminDashboardAnalytics(date?: string): Promise<AdminDashboardAnalytics> {
  const suffix = date ? `?date=${encodeURIComponent(date)}` : "";
  const body = await get<unknown>(`/admin/dashboard/analytics${suffix}`);
  return unwrapItem<AdminDashboardAnalytics>(body);
}

export async function fetchAdminTopCentrals(params?: {
  from?: string;
  to?: string;
  limit?: number;
}): Promise<TopCentral[]> {
  const q = new URLSearchParams();
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  if (params?.limit) q.set("limit", String(params.limit));
  const suffix = q.toString() ? `?${q}` : "";
  const body = await get<unknown>(`/admin/dashboard/top-centrals${suffix}`);
  const item = unwrapItem<TopCentral[] | { data?: TopCentral[] }>(body);
  return Array.isArray(item) ? item : (item.data ?? []);
}

// ——— Sessions ———

export async function fetchAdminSession(id: number): Promise<AdminSessionDetail> {
  const body = await get<unknown>(`/admin/sessions/${id}`);
  return unwrapItem<AdminSessionDetail>(body);
}

export async function fetchAdminSessions(params?: ListParams): Promise<Paginated<AdminSession>> {
  const body = await get<unknown>(`/admin/sessions${buildFilterQuery(params)}`);
  return normalizePaginated<AdminSession>(body);
}

export async function fetchAdminSessionsAnalytics(params?: {
  central_id?: number;
}): Promise<AdminSessionsAnalytics> {
  const q = new URLSearchParams();
  if (params?.central_id) q.set("central_id", String(params.central_id));
  const suffix = q.toString() ? `?${q}` : "";
  const body = await get<unknown>(`/admin/sessions/analytics${suffix}`);
  return unwrapItem<AdminSessionsAnalytics>(body);
}

// ——— Session actions ———

export async function approveAdminSession(id: number): Promise<AdminSessionDetail> {
  const body = await post<unknown>(`/admin/sessions/${id}/approve`, {});
  return unwrapItem<AdminSessionDetail>(body);
}

export async function rejectAdminSession(id: number, payload?: { reason?: string }): Promise<AdminSessionDetail> {
  const body = await post<unknown>(`/admin/sessions/${id}/reject`, payload ?? {});
  return unwrapItem<AdminSessionDetail>(body);
}

export async function endAdminSession(id: number): Promise<AdminSessionDetail> {
  const body = await post<unknown>(`/admin/sessions/${id}/end`, {});
  return unwrapItem<AdminSessionDetail>(body);
}

// ——— Central subscription ———

export async function fetchAdminCentralSubscription(centralId: number): Promise<AdminCentralSubscription | null> {
  try {
    const body = await get<unknown>(`/admin/centrals/${centralId}/subscription`);
    return unwrapItem<AdminCentralSubscription>(body);
  } catch (err: unknown) {
    if (isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

export async function renewAdminCentralSubscription(
  centralId: number,
  payload: AdminCentralSubscriptionRenewPayload,
): Promise<AdminCentralSubscription> {
  const body = await post<unknown>(`/admin/centrals/${centralId}/subscription`, payload);
  return unwrapItem<AdminCentralSubscription>(body);
}

// ——— Subscription requests ———

export async function fetchAdminSubscriptionRequests(
  params?: ListParams,
): Promise<Paginated<AdminSubscriptionRequest> & { pending?: number }> {
  const body = await get<Record<string, unknown>>(`/admin/subscription-requests${buildFilterQuery(params)}`);
  const page = normalizePaginated<AdminSubscriptionRequest>(body);
  const meta = (body.meta ?? {}) as Record<string, unknown>;
  return { ...page, pending: Number(meta.pending ?? 0) };
}

export async function fetchAdminSubscriptionRequestsPendingCount(): Promise<number> {
  const res = await fetchAdminSubscriptionRequests({ status: "pending", per_page: 1, page: 1 });
  return res.pending ?? 0;
}

export async function processAdminSubscriptionRequest(id: number): Promise<void> {
  await post(`/admin/subscription-requests/${id}/process`, {});
}

export async function cancelAdminSubscriptionRequest(id: number): Promise<void> {
  await post(`/admin/subscription-requests/${id}/cancel`, {});
}

// ——— Moderators & roles ———

export async function fetchAdminModerators(params?: ListParams): Promise<Paginated<AdminModerator>> {
  const body = await get<unknown>(`/admin/moderators${buildFilterQuery(params)}`);
  return normalizePaginated<AdminModerator>(body);
}

export async function fetchAdminModerator(id: number): Promise<AdminModerator> {
  const body = await get<unknown>(`/admin/moderators/${id}`);
  return unwrapItem<AdminModerator>(body);
}

export async function createAdminModerator(payload: AdminModeratorPayload): Promise<AdminModerator> {
  const body = await post<unknown>("/admin/moderators", payload);
  return unwrapItem<AdminModerator>(body);
}

export async function updateAdminModerator(id: number, payload: Partial<AdminModeratorPayload>): Promise<AdminModerator> {
  const body = await put<unknown>(`/admin/moderators/${id}`, payload);
  return unwrapItem<AdminModerator>(body);
}

export async function deleteAdminModerator(id: number): Promise<void> {
  await del(`/admin/moderators/${id}`);
}

export async function fetchAdminRoles(): Promise<AdminRole[]> {
  const body = await get<{ data?: AdminRole[] }>("/admin/roles");
  return body.data ?? [];
}

export async function assignAdminModeratorRole(id: number, role: string): Promise<void> {
  await post(`/admin/moderators/${id}/roles`, { role });
}

export async function removeAdminModeratorRole(id: number, role: string): Promise<void> {
  await del(`/admin/moderators/${id}/roles/${encodeURIComponent(role)}`);
}

// ——— Packages ———

export async function fetchAdminPackages(params?: ListParams): Promise<Paginated<AdminPackage>> {
  const body = await get<unknown>(`/admin/packages${buildFilterQuery(params)}`);
  return normalizePaginated<AdminPackage>(body);
}

export async function createAdminPackage(payload: AdminPackagePayload): Promise<AdminPackage> {
  const body = await post<unknown>("/admin/packages", payload);
  return unwrapItem<AdminPackage>(body);
}

export async function updateAdminPackage(id: number, payload: Partial<AdminPackagePayload>): Promise<AdminPackage> {
  const body = await put<unknown>(`/admin/packages/${id}`, payload);
  return unwrapItem<AdminPackage>(body);
}

export async function deleteAdminPackage(id: number): Promise<void> {
  await del(`/admin/packages/${id}`);
}

// ——— Zone Pricing ———

export async function fetchAdminZonePrices(params?: ListParams): Promise<Paginated<AdminZonePrice>> {
  const body = await get<unknown>(`/admin/zone-pricing${buildFilterQuery(params)}`);
  return normalizePaginated<AdminZonePrice>(body);
}

export async function createAdminZonePrice(payload: AdminZonePricePayload): Promise<AdminZonePrice> {
  const body = await post<unknown>("/admin/zone-pricing", payload);
  return unwrapItem<AdminZonePrice>(body);
}

export async function updateAdminZonePrice(id: number, payload: Partial<AdminZonePricePayload>): Promise<AdminZonePrice> {
  const body = await put<unknown>(`/admin/zone-pricing/${id}`, payload);
  return unwrapItem<AdminZonePrice>(body);
}

export async function deleteAdminZonePrice(id: number): Promise<void> {
  await del(`/admin/zone-pricing/${id}`);
}

// ——— Contract Regeneration ———

export interface ContractRegenSession {
  id: number;
  status: string;
  created_at: string;
  contracts_count: number;
  user: { id: number; name: string } | null;
  reviewer: { id: number; name: string } | null;
}

export interface ContractRegenResult {
  session_id: number;
  regenerated: number;
  failed: number;
  contracts: {
    contract_id: number;
    form_id: number;
    status: 'success' | 'error';
    pdf_url?: string;
    error?: string;
  }[];
}

export async function fetchSessionsBeforeDate(beforeDate: string, params?: ListParams): Promise<Paginated<ContractRegenSession>> {
  const query = buildFilterQuery(params);
  const separator = query ? '&' : '?';
  const body = await get<unknown>(`/admin/contract-regeneration/sessions${query}${separator}before_date=${encodeURIComponent(beforeDate)}`);
  return normalizePaginated<ContractRegenSession>(body);
}

export async function regenerateSessionPdfs(sessionId: number): Promise<ContractRegenResult> {
  const body = await post<unknown>(`/admin/contract-regeneration/sessions/${sessionId}/regenerate`, {});
  return unwrapItem<ContractRegenResult>(body);
}

// ——— Promo Codes ———

export async function fetchAdminPromoCodes(params?: ListParams): Promise<Paginated<AdminPromoCode>> {
  const body = await get<unknown>(`/admin/promo-codes${buildFilterQuery(params)}`);
  return normalizePaginated<AdminPromoCode>(body);
}

export async function createAdminPromoCode(payload: AdminPromoCodePayload): Promise<AdminPromoCode> {
  const body = await post<unknown>("/admin/promo-codes", payload);
  return unwrapItem<AdminPromoCode>(body);
}

export async function updateAdminPromoCode(id: number, payload: Partial<AdminPromoCodePayload>): Promise<AdminPromoCode> {
  const body = await put<unknown>(`/admin/promo-codes/${id}`, payload);
  return unwrapItem<AdminPromoCode>(body);
}

export async function deleteAdminPromoCode(id: number): Promise<void> {
  await del(`/admin/promo-codes/${id}`);
}

// ——— Subscriptions ———

export async function fetchAdminSubscriptions(params?: ListParams): Promise<Paginated<AdminSubscription>> {
  const suffix = buildQuery(params, { date_from: "from", date_to: "to" });
  const body = await get<unknown>(`/admin/subscriptions${suffix}`);
  return normalizePaginated<AdminSubscription>(body);
}

export async function fetchAdminDoctorSubscriptions(doctorId: number): Promise<AdminSubscription[]> {
  const body = await get<{ data?: AdminSubscription[] }>(`/admin/doctors/${doctorId}/subscriptions`);
  return body.data ?? [];
}

export async function fetchAdminDoctorSubscriptionsList(
  params?: ListParams,
): Promise<Paginated<AdminDoctorSubscription>> {
  const suffix = buildQuery(params, { date_from: "from", date_to: "to" });
  const body = await get<unknown>(`/admin/doctor-subscriptions${suffix}`);
  return normalizePaginated<AdminDoctorSubscription>(body);
}

export async function createAdminSubscription(payload: AdminSubscriptionPayload): Promise<AdminSubscription> {
  const body = await post<unknown>("/admin/subscriptions", payload);
  return unwrapItem<AdminSubscription>(body);
}

export async function updateAdminSubscription(id: number, payload: Partial<AdminSubscriptionPayload>): Promise<AdminSubscription> {
  const body = await put<unknown>(`/admin/subscriptions/${id}`, payload);
  return unwrapItem<AdminSubscription>(body);
}

// ——— Pending field changes ———

export async function fetchAdminPendingFieldChanges(
  params?: ListParams,
): Promise<Paginated<AdminPendingFieldChange> & { pending?: number }> {
  const body = await get<Record<string, unknown>>(`/admin/pending-field-changes${buildFilterQuery(params)}`);
  const page = normalizePaginated<AdminPendingFieldChange>(body);
  const meta = (body.meta ?? {}) as Record<string, unknown>;
  return { ...page, pending: Number(meta.pending ?? 0) };
}

export async function fetchAdminPendingFieldChangesPendingCount(): Promise<number> {
  const res = await fetchAdminPendingFieldChanges({ status: "pending", per_page: 1, page: 1 });
  return res.pending ?? 0;
}

export async function approveAdminPendingFieldChange(id: number, note?: string): Promise<void> {
  await post(`/admin/pending-field-changes/${id}/approve`, note ? { note } : {});
}

export async function rejectAdminPendingFieldChange(id: number, note: string): Promise<void> {
  await post(`/admin/pending-field-changes/${id}/reject`, { note });
}

// ——— Audit log ———

export async function fetchAdminAudit(
  params?: ListParams,
): Promise<Paginated<AdminAuditEntry> & { filters?: AdminAuditFilters }> {
  const suffix = buildQuery(params, { date_from: "from", date_to: "to" });
  const body = await get<Record<string, unknown>>(`/admin/audit${suffix}`);
  const page = normalizePaginated<AdminAuditEntry>(body);
  const filters = (body.filters ?? {}) as AdminAuditFilters;
  return { ...page, filters };
}

// ——— Notifications ———

export async function fetchAdminNotifications(params?: ListParams): Promise<Paginated<AdminNotification>> {
  const body = await get<unknown>(`/admin/notifications${buildFilterQuery(params)}`);
  return normalizePaginated<AdminNotification>(body);
}

export async function sendAdminNotification(
  payload: AdminNotificationSendPayload,
): Promise<AdminNotificationSendResult> {
  const body = await post<Record<string, unknown>>("/admin/notifications/send", payload);
  const notification = unwrapItem<AdminNotification>(body);
  const queued = Number(body.queued ?? 0);
  return { notification, queued };
}

export async function deleteAdminNotification(id: number): Promise<void> {
  await del(`/admin/notifications/${id}`);
}

// ——— Settings ———

export async function fetchAdminSettings(): Promise<AdminSettingsGroups> {
  const body = await get<unknown>("/admin/settings");
  const root = body as Record<string, unknown>;
  const data = (root.data ?? body) as Record<string, unknown>;
  return {
    general: (data.general as AdminSettingEntry[]) ?? [],
    social:  (data.social  as AdminSettingEntry[]) ?? [],
    app:     (data.app     as AdminSettingEntry[]) ?? [],
  };
}

export async function updateAdminSettings(settings: AdminSettingEntry[]): Promise<AdminSettingsGroups> {
  const body = await post<unknown>("/admin/settings", { settings });
  const root = body as Record<string, unknown>;
  const data = (root.data ?? body) as Record<string, unknown>;
  return {
    general: (data.general as AdminSettingEntry[]) ?? [],
    social:  (data.social  as AdminSettingEntry[]) ?? [],
    app:     (data.app     as AdminSettingEntry[]) ?? [],
  };
}

// ——— Doctor Ranking (global leaderboard) ———

export interface RankingLeaderboardRow {
  rank: number;
  doctor_id: number;
  name: string;
  avatar: string | null;
  specialization_name: string | null;
  central_name: string | null;
  city_name: string | null;
  count: number;
  delta: number | null;
}

export interface RankingLeaderboardFilters {
  centrals: { id: number; name: string }[];
  cities: { id: number; name: string }[];
  specializations: { id: number; name: string }[];
}

export interface RankingLeaderboardData {
  period: string | null;
  periods: string[];
  rows: RankingLeaderboardRow[];
  filters: RankingLeaderboardFilters;
  totals: { doctors: number; signed: number };
  meta: { total: number; current_page: number; last_page: number; per_page: number };
}

export interface RankingLeaderboardParams {
  period?: string;
  central_id?: number;
  city_id?: number;
  specialization_id?: number;
  /** "independent" = no central · "center" = affiliated with a central */
  doctor_type?: "independent" | "center";
  page?: number;
  per_page?: number;
}

export async function fetchRankingLeaderboard(
  params: RankingLeaderboardParams = {},
): Promise<RankingLeaderboardData> {
  const q = new URLSearchParams();
  if (params.period) q.set("period", params.period);
  if (params.central_id) q.set("central_id", String(params.central_id));
  if (params.city_id) q.set("city_id", String(params.city_id));
  if (params.specialization_id) q.set("specialization_id", String(params.specialization_id));
  if (params.doctor_type) q.set("doctor_type", params.doctor_type);
  if (params.page) q.set("page", String(params.page));
  if (params.per_page) q.set("per_page", String(params.per_page));
  const qs = q.toString();
  const body = await get<unknown>(`/admin/ranking/leaderboard${qs ? `?${qs}` : ""}`);
  return unwrapItem<RankingLeaderboardData>(body);
}
